import { DO_CONTRACT_TYPES } from "@etudescarre/constants";
import { cn, Drawer } from "@etudescarre/ui";
import { XIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useParams } from "react-router";
import { useGuardedNavigate } from "@/lib/utils/unsaved-changes";
import { useYardContext } from "@/providers/yard-context";
import { ConsignerVisaPanel } from "./components/consigner-visa";
import { DepositReportPanel } from "./components/deposit-report";
import { ProposalSendBar } from "./components/document-table/proposal-send-bar";
import { ProposeDocumentsPanel } from "./components/propose-documents";
import { ProposeMaquettePanel } from "./components/propose-maquette";
import { RegisterFtmPanel } from "./components/register-ftm";
import { RegisterOsPanel } from "./components/register-os";
import { RegisterReceptionForm } from "./components/register-reception";
import { RegisterReportPanel } from "./components/register-report";

type ActionItem = {
  label: string;
  action: string;
};

type ActionSection = {
  label: string;
  colorClass: string;
  items: ActionItem[];
  visible: boolean;
};

const RANG_2_SECTION = "Action rang 2";

function useActionSections(): ActionSection[] {
  const { hasContractType } = useYardContext();

  const hasTravaux =
    hasContractType("travaux_n_1") || hasContractType("travaux_n_2");
  const hasTravauxN1 = hasContractType("travaux_n_1");
  const hasTravauxN2 = hasContractType("travaux_n_2");
  const hasDonneurOrdre = DO_CONTRACT_TYPES.some((t) => hasContractType(t));

  return [
    {
      label: "Action donneur d'ordre",
      colorClass: "text-purple-600 dark:text-purple-400",
      visible: hasDonneurOrdre,
      items: [
        // Écran 033 : dépôt direct avec méta-données auto-renseignées.
        { label: "Déposer un rapport d'examen", action: "deposer-rapport" },
      ],
    },
    {
      label: "Action client",
      colorClass: "text-blue-600 dark:text-blue-400",
      visible: hasTravaux,
      items: [
        // The FTM and OS routes only accept a travaux rang 1 contract.
        ...(hasTravauxN1
          ? [
              {
                label: "Enregistrer la réception d'une FTM",
                action: "enregistrer-ftm",
              },
              {
                label: "Enregistrer la réception d'un OS",
                action: "enregistrer-os",
              },
            ]
          : []),
        { label: "Diffuser pour avis", action: "diffuser-avis" },
        {
          label: "Enregistrer rapport examen reçu",
          action: "enregistrer-rapport",
        },
        {
          label: "Réceptionner rapport via sas DO",
          action: "receptionner-rapport",
        },
        {
          label: "Répondre à observations",
          action: "repondre-observations",
        },
        {
          label: "Préparer mises au courrier",
          action: "preparer-courrier",
        },
        {
          label: "Enregistrer mises au courrier",
          action: "enregistrer-courrier",
        },
        {
          label: "Enregistrer remise en main propre",
          action: "remise-main-propre",
        },
        {
          label: "Remettre en main propre un courrier",
          action: "remise-courrier-main-propre",
        },
        {
          label: "Préparer remises en main propre",
          action: "preparer-main-propre",
        },
      ],
    },
    {
      label: "Action sous-traitants",
      colorClass: "text-emerald-600 dark:text-emerald-400",
      visible: hasTravauxN1,
      items: [
        {
          label: "Enregistrer une réception de documents et/ou maquettes",
          action: "enregistrer-reception",
        },
        {
          label: "Réceptionner documents via sas",
          action: "receptionner-docs-sas",
        },
        {
          label: "Consigner visa entreprise générale",
          action: "consigner-visa",
        },
        {
          label: "Générer visa entreprise générale",
          action: "generer-visa",
        },
        {
          label: "Diffuser pour coordination",
          action: "diffuser-coordination",
        },
      ],
    },
    {
      label: RANG_2_SECTION,
      colorClass: "text-amber-600 dark:text-amber-400",
      visible: hasTravauxN2,
      items: [
        {
          label: "Proposer des documents",
          action: "proposer-docs",
        },
        {
          label: "Ajouter une maquette à la proposition",
          action: "proposer-maquette",
        },
        {
          label: "Ajouter des documents et la maquette associée",
          action: "proposer-maquette-documents",
        },
        { label: "Voir mes propositions", action: "mes-propositions" },
      ],
    },
  ];
}

type YardActionsProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function YardActions({ open, onOpenChange }: YardActionsProps) {
  const sections = useActionSections();
  const [activeAction, setActiveAction] = useState<string | null>(null);
  const { yardId } = useParams<{ yardId: string }>();
  const navigate = useGuardedNavigate();

  const handleAction = (action: string) => {
    // Screens 019/020 live on their own routes; these action buttons are their
    // access points.
    if (action === "diffuser-avis") {
      navigate(`/yards/${yardId}/diffusion-do`);
      onOpenChange(false);
      return;
    }
    // Screen 040 — the rang 2 proposals and their state, own route.
    if (action === "mes-propositions") {
      navigate(`/yards/${yardId}/propositions`);
      onOpenChange(false);
      return;
    }
    // Screen 016 — réception hors ÉtudesCarré, own full-width route.
    if (action === "enregistrer-reception") {
      navigate(`/yards/${yardId}/enregistrer-reception`);
      onOpenChange(false);
      return;
    }
    // Screen 017 — réception via sas prestataires, own route.
    if (action === "receptionner-docs-sas") {
      navigate(`/yards/${yardId}/reception-sas`);
      onOpenChange(false);
      return;
    }
    // Screen 021bis lives on its own route; the action button is its access point.
    if (action === "remise-main-propre") {
      navigate(`/yards/${yardId}/remise-main-propre`);
      onOpenChange(false);
      return;
    }
    // Screen 021ter lives on its own route; the action button is its access point.
    if (action === "remise-courrier-main-propre") {
      navigate(`/yards/${yardId}/remise-courrier-main-propre`);
      onOpenChange(false);
      return;
    }
    if (action === "diffuser-coordination") {
      navigate(`/yards/${yardId}/diffusion-presta`);
      onOpenChange(false);
      return;
    }
    // Screen 021 lives on its own route; the action button is its access point.
    if (action === "preparer-courrier") {
      navigate(`/yards/${yardId}/mises-au-courrier`);
      onOpenChange(false);
      return;
    }
    // Screen 022 lives on its own route; the action button is its access point.
    if (action === "enregistrer-courrier") {
      navigate(`/yards/${yardId}/mises-au-courrier-effective`);
      onOpenChange(false);
      return;
    }
    // Screen 021bis lives on its own route; the action button is its access point.
    if (action === "preparer-main-propre") {
      navigate(`/yards/${yardId}/remises-en-main-propre`);
      onOpenChange(false);
      return;
    }
    setActiveAction(action);
  };

  return (
    <Drawer.Root open={open} onOpenChange={(e) => onOpenChange(e.open)}>
      <Drawer.Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          {/* This form is laid out for the full-width screen 016. */}
          <Drawer.Content
            style={
              activeAction === "proposer-maquette-documents"
                ? { width: "min(72rem, 100vw)" }
                : undefined
            }
          >
            <div className="flex items-center justify-between border-b border-zinc-200 px-4 py-3 dark:border-zinc-800">
              <Drawer.Title>Actions</Drawer.Title>
              <Drawer.CloseTrigger>
                <XIcon className="size-4" />
              </Drawer.CloseTrigger>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {activeAction ? (
                activeAction === "enregistrer-ftm" ? (
                  <RegisterFtmPanel onClose={() => setActiveAction(null)} />
                ) : activeAction === "enregistrer-os" ? (
                  <RegisterOsPanel
                    onClose={() => setActiveAction(null)}
                    onSaved={() => {
                      setActiveAction(null);
                      onOpenChange(false);
                      // A new history entry reloads the classeur even when
                      // it is already the current page.
                      navigate(`/yards/${yardId}/os`);
                    }}
                  />
                ) : activeAction === "enregistrer-rapport" ? (
                  <RegisterReportPanel onClose={() => setActiveAction(null)} />
                ) : activeAction === "deposer-rapport" ? (
                  <DepositReportPanel onClose={() => setActiveAction(null)} />
                ) : activeAction === "consigner-visa" ? (
                  <ConsignerVisaPanel onClose={() => setActiveAction(null)} />
                ) : activeAction === "proposer-docs" ? (
                  <ProposeDocumentsPanel
                    onClose={() => setActiveAction(null)}
                  />
                ) : activeAction === "proposer-maquette" ? (
                  <ProposeMaquettePanel onClose={() => setActiveAction(null)} />
                ) : activeAction === "proposer-maquette-documents" ? (
                  <RegisterReceptionForm
                    proposal
                    onClose={() => setActiveAction(null)}
                  />
                ) : (
                  <div className="space-y-4">
                    <button
                      type="button"
                      onClick={() => setActiveAction(null)}
                      className="text-sm text-blue-600 hover:underline dark:text-blue-400"
                    >
                      Retour aux actions
                    </button>
                    <div className="rounded-lg border border-zinc-200 p-6 text-center dark:border-zinc-700">
                      <p className="text-sm text-zinc-500">
                        En cours de developpement
                      </p>
                    </div>
                  </div>
                )
              ) : (
                sections
                  .filter((s) => s.visible)
                  .map((section) => (
                    <div key={section.label} className="mb-6">
                      <h3
                        className={cn(
                          "mb-2 text-xs font-bold uppercase tracking-wider",
                          section.colorClass,
                        )}
                      >
                        {section.label}
                      </h3>
                      <ul className="space-y-0.5">
                        {section.items.map((item) => (
                          <li key={item.action}>
                            <button
                              type="button"
                              onClick={() => handleAction(item.action)}
                              className="w-full rounded-md px-3 py-2 text-left text-sm text-zinc-700 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800"
                            >
                              {item.label}
                            </button>
                          </li>
                        ))}
                      </ul>
                      {section.label === RANG_2_SECTION && (
                        <ProposalSendBar onSent={() => onOpenChange(false)} />
                      )}
                    </div>
                  ))
              )}
            </div>
          </Drawer.Content>
        </Drawer.Positioner>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
